import { useEditor } from "@tiptap/react";
import { useState, useEffect, useRef } from "react"
import Sheet from 'react-modal-sheet';
import Tiptap from "../../shared/components/editor/Tiptap";
import StarterKit from "@tiptap/starter-kit"
import { TextStyleKit } from '@tiptap/extension-text-style'
import ZipmapRepository from "../../repository/ZipmapRepository";
import EditButton from "../../shared/components/dashboard/EditButton";
import LocationPickerModal from "../locationpicker/LocationPickerModal";
import Link from "@tiptap/extension-link";
import { useSensors, useSensor, PointerSensor, KeyboardSensor, DndContext, closestCenter } from '@dnd-kit/core';
import { sortableKeyboardCoordinates, arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import SortableItem from "../../shared/components/sortable/SortableItem";
import data from '@emoji-mart/data'
import Picker from '@emoji-mart/react'
import { downsizeImage } from "../../shared/utils/ImageResizeUtils";
import Datetime from "react-datetime";

const extensions = [
    TextStyleKit, 
    StarterKit,
    Link.configure({
        openOnClick: false,  // Don't open links when clicked in editor
        HTMLAttributes: {
          class: 'text-blue-500 underline',  // Optional styling
        },
      })
]

export default function DashboardEditPlaceModal(
    { 
        isEditPlaceModalOpen,
        onPlaceUpdated,
        onEditPlaceModalDismiss,
        mapDetails,
        initialPlace,
        onImageClick
    }
) {

    const [isUsingDarkMode, setIsUsingDarkMode] = useState(false)
    const [toastMessage, setToastMessage] = useState(null)
    const [place, setPlace] = useState(initialPlace ?? {
        id: 0,
        name: "",
        description: "",
        longDescription: "",
        coordinates: {
            latitude: 1.323139498798759,
            longitude: 103.8047231806979
        },
        images: [],
        markerEmoji: null
    })
    const placeRef = useRef(initialPlace ?? {
        id: 0,
        name: "",
        description: "",
        longDescription: "",
        coordinates: {
            latitude: 1.323139498798759,
            longitude: 103.8047231806979
        },
    })
    const [placeImages, setPlaceImages] = useState(initialPlace ? initialPlace.images : [])
    const [fullScreenLoadingText, setFullScreenLoadingText] = useState(null)
    const [isLocationPickerModalOpen, setIsLocationPickerModalOpen] = useState(false)
    const [selectedLocation, setSelectedLocation] = useState(null)
    const [selectedSpecialEffect, setSelectedSpecialEffect] = useState(initialPlace ? initialPlace.markerEffectType : "NONE")
    const [specialEffectPeriod, setSpecialEffectPeriod] = useState("indefinite")
    const [specialEffectFrom, setSpecialEffectFrom] = useState(null)
    const [specialEffectTo, setSpecialEffectTo] = useState(null)

    const editor = useEditor({
        extensions: extensions,
        content: initialPlace ? initialPlace.longDescription : "",
        editorProps: {
            attributes: {
                class: 'w-full min-h-[300px]',
            },
        },
    })

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
          coordinateGetter: sortableKeyboardCoordinates,
        })
    );
    

    useEffect(() => {
        if (initialPlace) {
        setPlace(initialPlace)
            editor.commands.setContent(initialPlace.longDescription)
            setPlaceImages(initialPlace.images)
        }
    }, [initialPlace])

    useEffect(() => {
        if (toastMessage) {
            setTimeout(() => {
                setToastMessage(null)
            }, 3000)
        }
    }, [toastMessage])

    useEffect(() => {
        setIsUsingDarkMode(window.matchMedia('(prefers-color-scheme: dark)').matches)
    }, [])

    useEffect(() => {
        placeRef.current = place
    }, [place])

    function onPlaceNameInputChange(event) {
        setPlace({ ...placeRef.current, name: event.target.value })
    }

    function onShortDescriptionInputChange(event) {
        setPlace({ ...placeRef.current, description: event.target.value })
    }

    async function onFileInputChange(event) {
        const imageFiles = []
        for (let i = 0; i < event.target.files.length; i++) {
            const file = event.target.files[i]
            const resizedBlob = await downsizeImage(file);
            console.log('Original size:', (file.size / 1024).toFixed(2), 'KB');
            console.log('Resized size:', (resizedBlob.size / 1024).toFixed(2), 'KB');

            const url = URL.createObjectURL(resizedBlob)
            imageFiles.push(
                {file: resizedBlob, url: url}
            )
        }
        setPlaceImages([...placeImages, ...imageFiles])
    }

    async function onEditPlaceSaveClick() {

        if (placeRef.current.name.trim() === "") {
            setToastMessage({
                message: "⚠️ Please enter a place name",
                type: "info"
            })
            return
        }

        setFullScreenLoadingText("Saving ...")
        const zipmapRepository = new ZipmapRepository()
        if (placeRef.current.id > 0) {
            const updatedPlace = await zipmapRepository.editPlace(
                mapDetails.slug, 
                placeRef.current.id, 
                placeImages.map((image, index) => {
                    return {
                        file: image.file,
                        url: image.url,
                        name: image.file ? `image${index}` : null
                    }
                }),
                placeRef.current.name,
                place.description,
                editor.getHTML(),
                place.coordinates,
                place.markerEmoji,
                selectedSpecialEffect
            )
            onPlaceUpdated(updatedPlace.place)
        } else {
            const addedPlace = await zipmapRepository.addPlace(
                mapDetails.slug,
                placeImages.map((image, index) => {
                    return {
                        file: image.file,
                        url: image.url,
                        name: image.file ? `image${index}` : null
                    }
                }),
                placeRef.current.name,
                place.description,
                editor.getHTML(),
                placeRef.current.coordinates,
                placeRef.current.markerEmoji,
                selectedSpecialEffect
            )
            onPlaceUpdated(addedPlace.place)
        }
        setFullScreenLoadingText(null)
    }

    function onLocationPickerModalDismiss() {
        setIsLocationPickerModalOpen(false)
    }

    function onEditLocationClick() {
        console.log("on edit location click")
        setSelectedLocation({
            label: placeRef.current.name,
            coordinates: {
                latitude: placeRef.current.coordinates.latitude,
                longitude: placeRef.current.coordinates.longitude
            }
        })
        setIsLocationPickerModalOpen(true)
    }
    
    function onLocationSelected(location) {
        setIsLocationPickerModalOpen(false)
        setPlace({ ...placeRef.current, coordinates: location })
    }

    function onAddImageClick() {
        document.getElementById('upload-place-image-input').click()
    }

    function handleDragEnd(event) {
        const { active, over } = event;
    
        if (active.id !== over.id) {
          setPlaceImages((placeImages) => {
            const oldIndex = placeImages.findIndex((image) => image.url === active.id);
            const newIndex = placeImages.findIndex((image) => image.url === over.id);
            return arrayMove(placeImages, oldIndex, newIndex);
          });
        }
    }

    function onDeleteImageClick(index) {
        console.log("on delete image click", index)
        setPlaceImages(placeImages.filter((image, i) => i !== index))
    }

    function onEditPlaceMarkerClick() {
        document.getElementById("pick-place-emoji-modal").showModal()
    }

    function onEmojiSelected(emoji) {
        // setMapDefaultMarker(emojiData.emoji)
        document.getElementById("pick-place-emoji-modal").close()
        setPlace({ ...placeRef.current, markerEmoji: emoji })
    }

    function onSpecialEffectClick(effect) {
        setSelectedSpecialEffect(effect)
    }

    function onSpecialEffectPeriodClick(period) {
        setSpecialEffectPeriod(period)
    }

    function onSpecialEffectFromChange(value) {
        console.log("on special effect from change", value.format("YYYY-MM-DDTHH:MM:SS.ssssss+TZ"))
        setSpecialEffectFrom(value)
    }

    function onSpecialEffectToChange(value) {
        console.log("on special effect to change", value.format("YYYY-MM-DDTHH:MM:SS.ssssss+TZ"))
        setSpecialEffectTo(value)
    }

    return <><Sheet
     isOpen={isEditPlaceModalOpen} onClose={() => {onEditPlaceModalDismiss()}} onOpenEnd={() => {}} detent="full-height"
     >
        <Sheet.Container>
        <Sheet.Header />
        <Sheet.Content className="bg-base-300">
        <Sheet.Scroller draggableAt="both">
        <div className="p-4 h-full">
        <div className="pb-8 px-4">
            <div className="font-bold text-xl pb-2 flex items-center font-titan">
                <span className="pr-2">✏️</span>
                { place.id > 0 ? "Edit Place" : "Add Place" }
            </div>
            <div className="font-bold text-base pb-2 flex items-center">
                <span>Place Name</span>
            </div>
            <div className="flex w-full gap-2">
                <div className="form-control flex-1 relative flex flex-row items-center gap-2">
                    <input className={`input input-bordered flex-1 rounded-md z-1`} value={place.name} placeholder={"Enter place name here ..."} onChange={onPlaceNameInputChange} />
                </div>
            </div>

            <div className="font-bold text-base pb-2 pt-8 flex items-center">
                <span>Location</span>
            </div>
            {
                place.coordinates && <div className="text-base-content pb-2">
                    Coordinates: {place.coordinates.latitude.toFixed(5)}, {place.coordinates.longitude.toFixed(5)}
                </div>
            }
            {
                place.coordinates && isUsingDarkMode && <img className="rounded-md" src={`https://api.mapbox.com/styles/v1/mapbox/dark-v11/static/url-https%3A%2F%2Ftwblyxzvdbhlhxxurpno.supabase.co%2Fstorage%2Fv1%2Fobject%2Fpublic%2Fzipmap-resources%2Fpushpin.png(${place.coordinates.longitude},${place.coordinates.latitude})/${place.coordinates.longitude},${place.coordinates.latitude},16,0/600x300?access_token=pk.eyJ1IjoiamV5ZWUiLCJhIjoiY2o0MmwxYmozMGo5ZTM4cXE1c2QwcnU3bSJ9.2QOe2Nr2XyVlwUmJLTCAnQ`} />

            }
            {
                place.coordinates && !isUsingDarkMode && <img className="rounded-md" src={`https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/url-https%3A%2F%2Ftwblyxzvdbhlhxxurpno.supabase.co%2Fstorage%2Fv1%2Fobject%2Fpublic%2Fzipmap-resources%2Fpushpin.png(${place.coordinates.longitude},${place.coordinates.latitude})/${place.coordinates.longitude},${place.coordinates.latitude},16,0/600x300?access_token=pk.eyJ1IjoiamV5ZWUiLCJhIjoiY2o0MmwxYmozMGo5ZTM4cXE1c2QwcnU3bSJ9.2QOe2Nr2XyVlwUmJLTCAnQ`} />

            }
            <div className="flex w-full gap-2 pt-4">
                <EditButton onEditClick={() => {onEditLocationClick()}} />
            </div>

            <div className="text-left text-md text-base-content/70 pt-8 pb-1">
                Place marker
            </div>
            <div className="flex flex-row items-center">
                <div className="font-bold text-left text-4xl pr-4">
                    {place.markerEmoji ?? mapDetails.defaultMarker}
                </div>
                <div className="shrink-0 "><EditButton onEditClick={onEditPlaceMarkerClick} /></div>
            </div>
            { !place.markerEmoji && <div className="text-left text-sm text-base-content/50 pb-2 pt-2">
                Using default map marker emoji
            </div> }

            <div className="font-bold text-base pb-2 pt-8 flex items-center">
                <span>Images</span>
            </div>
            <input id="upload-place-image-input" type="file" accept="image/png, image/jpeg, image/webp" className="file-input file-input-bordered w-full max-w-xs hidden" onChange={onFileInputChange} multiple/>
            <div className="flex">
                {
                    placeImages.length > 0 && <div className="flex-none">
                        <button className="btn btn-ghost h-24 w-24 p-0 rounded-md border-base-content border-dashed border-2 " onClick={() => {onAddImageClick()}}>
                        <div className="h-20 w-20 flex place-content-center items-center text-2xl font-bold rounded-md ">+</div>
                        </button>
                    </div>
                }
                <div id="place-images-container" className="carousel rounded-md h-24 w-full space-x-2 grow overflow-x-scroll">
                {
                    placeImages.length != 0 && <div className="carousel-item w-2 rounded-md"></div>
                }
                {
                    placeImages.length == 0 && <div className="carousel-item">
                        <button className="btn btn-ghost h-24 w-32 p-0 rounded-md border-base-content border-dashed border-2 normal-case" onClick={() => {document.getElementById('upload-place-image-input').click();}}>
                        <div className="h-20 w-32 flex place-content-center items-center text-md font-bold rounded-md ">
                        <svg className="h-4 w-4 mr-1" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path className="stroke-base-content group-hover:stroke-base-100" d="M14.2647 15.9377L12.5473 14.2346C11.758 13.4519 11.3633 13.0605 10.9089 12.9137C10.5092 12.7845 10.079 12.7845 9.67922 12.9137C9.22485 13.0605 8.83017 13.4519 8.04082 14.2346L4.04193 18.2622M14.2647 15.9377L14.606 15.5991C15.412 14.7999 15.8149 14.4003 16.2773 14.2545C16.6839 14.1262 17.1208 14.1312 17.5244 14.2688C17.9832 14.4253 18.3769 14.834 19.1642 15.6515L20 16.5001M14.2647 15.9377L18.22 19.9628M18.22 19.9628C17.8703 20 17.4213 20 16.8 20H7.2C6.07989 20 5.51984 20 5.09202 19.782C4.7157 19.5903 4.40973 19.2843 4.21799 18.908C4.12583 18.7271 4.07264 18.5226 4.04193 18.2622M18.22 19.9628C18.5007 19.9329 18.7175 19.8791 18.908 19.782C19.2843 19.5903 19.5903 19.2843 19.782 18.908C20 18.4802 20 17.9201 20 16.8V13M11 4H7.2C6.07989 4 5.51984 4 5.09202 4.21799C4.7157 4.40973 4.40973 4.71569 4.21799 5.09202C4 5.51984 4 6.0799 4 7.2V16.8C4 17.4466 4 17.9066 4.04193 18.2622M18 9V6M18 6V3M18 6H21M18 6H15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg> Add images</div>
                        </button>
                    </div>
                }
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                    >
                    <SortableContext
                        items={placeImages.map((image) => image.url)}
                        strategy={verticalListSortingStrategy}
                    >
                {
                    placeImages.map((image, index) => {
                        return <SortableItem key={image.url} id={image.url} isDisabled={false}><div className="carousel-item relative w-24 h-24" id={`thumbnail-${index}`} key={`thumbnail-${index}`} onClick={() => {
                            onImageClick(placeImages, index)
                        }}>
                            <img className="h-24 w-24 object-cover rounded-md" src={image.url} />
                            <button 
                            className="btn btn-ghost btn-circle btn-sm absolute top-0 right-0 z-10" 
                            onClick={(e) => {
                                e.stopPropagation();
                                onDeleteImageClick(index)
                            }}
                            onPointerDown={(e) => e.stopPropagation()} 
                            >
                                <svg className="w-4 h-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path className="stroke-base-content" d="M6 18L18 6M6 6l12 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                        </div></SortableItem>
                    })
                }
                </SortableContext>
                </DndContext>
                </div>
            </div>

            <div className="font-bold text-md pb-2 flex items-center pt-8">
                <span>Short Description</span>
            </div>
            <div className="flex w-full gap-2">
                <div className="form-control flex-1 relative flex flex-row items-center gap-2">
                    <input className={`input input-bordered flex-1 rounded-md z-1`} value={place.description} placeholder={"Enter short description here ..."} onChange={onShortDescriptionInputChange} />
                </div>
            </div>

            <div className="font-bold text-base flex items-center pt-8">
                <span>Long Description</span>
            </div>
            <div className="flex w-full gap-2">
                <div className="form-control flex-1">
                    <Tiptap editor={editor} />
                </div>
            </div>

            <div className="font-bold text-base flex items-center pt-8 pb-2">
                <span>Special Effect</span>
            </div>
            <div className="flex w-max flex-col gap-2 pb-24">
                <div className="form-control" onClick={(e) => { onSpecialEffectClick("NONE") }}>
                    <label className="label cursor-pointer px-0">
                        <input type="radio" name="radio-wiggle" className="radio checked:bg-primary" checked={selectedSpecialEffect == "NONE"} />
                        <div className="flex flex-row gap-2 items-center justify-center pl-4">
                        <span className="label-text text-base">None</span>
                        <div className="text-5xl">{place.markerEmoji ?? mapDetails.defaultMarker}</div>
                        </div>
                    </label>
                </div>
                <div className="form-control" onClick={(e) => { onSpecialEffectClick("WIGGLE") }}>
                    <label className="label cursor-pointer px-0">
                        <input type="radio" name="radio-wiggle" className="radio checked:bg-primary" checked={selectedSpecialEffect == "WIGGLE"} />
                        <div className="flex flex-row gap-2 items-center justify-center pl-4">
                        <span className="label-text text-base">Wiggle</span>
                        <div className="text-5xl animate-wiggle animate-infinite animate-ease-in-out">{place.markerEmoji ?? mapDetails.defaultMarker}</div>
                        </div>
                    </label>
                </div>
                <div className="form-control" onClick={(e) => { onSpecialEffectClick("PULSE") }}>
                    <label className="label cursor-pointer px-0">
                        <input type="radio" name="radio-pulse" className="radio checked:bg-primary" checked={selectedSpecialEffect == "PULSE"} />
                        <div className="flex flex-row gap-2 items-center justify-center pl-4">
                        <span className="label-text text-base">Pulse</span>
                        <div className="text-5xl animate-pulse animate-infinite animate-ease-in-out">{place.markerEmoji ?? mapDetails.defaultMarker}</div>
                        </div>
                    </label>
                </div>
                <div className="form-control" onClick={(e) => { onSpecialEffectClick("BOUNCE") }}>
                    <label className="label cursor-pointer px-0">
                        <input type="radio" name="radio-bounce" className="radio checked:bg-primary" checked={selectedSpecialEffect == "BOUNCE"} />
                        <div className="flex flex-row gap-2 items-center justify-center pl-4">
                        <span className="label-text text-base">Bounce</span>
                        <div className="text-5xl animate-bounce animate-infinite animate-ease-in-out">{place.markerEmoji ?? mapDetails.defaultMarker}</div>
                        </div>
                    </label>
                </div>
            </div>

            {/* <div className="font-bold text-base flex items-center pt-4 pb-2">
                <span>Special Effect Period</span>
            </div>
            <div className="flex w-max pb-24 flex-col gap-2">
            <div className="form-control" onClick={(e) => { onSpecialEffectPeriodClick("indefinite") }}>
                    <label className="label cursor-pointer px-0">
                        <input type="radio" name="radio-indefinite" className="radio checked:bg-primary" checked={specialEffectPeriod == "indefinite"} />
                        <div className="flex flex-row gap-2 items-center justify-center pl-4">
                        <span className="label-text text-base">Indifinite</span>
                        </div>
                    </label>
                </div>
                <div className="form-control" onClick={(e) => { onSpecialEffectPeriodClick("custom") }}>
                    <label className="label cursor-pointer px-0">
                        <input type="radio" name="radio-custom" className="radio checked:bg-primary" checked={specialEffectPeriod == "custom"} />
                        <div className="flex flex-row gap-2 items-center justify-center pl-4">
                        <span className="label-text text-base">Within a period</span>
                        </div>
                    </label>
                </div>
                <div>
                {
                    specialEffectPeriod == "custom" && <div className="grid grid-cols-2 gap-2">
                        <div className="col-span-1 flex flex-col">
                            <span className="label-text text-base pb-1">From</span>
                            <Datetime
                             dateFormat="DD-MM-YYYY"
                             value={specialEffectFrom} 
                             onChange={(value) => { onSpecialEffectFromChange(value) }} />
                        </div>
                        <div className="col-span-1 flex flex-col">
                            <span className="label-text text-base pb-1">To</span>
                            <Datetime 
                            dateFormat="DD-MM-YYYY"
                            value={specialEffectTo} 
                            onChange={(value) => { onSpecialEffectToChange(value) }} />
                        </div>
                    </div>
                }
                </div>
            </div> */}
        </div>
        <div className="modal-action bg-base-100 w-full px-4 py-4 absolute bottom-0 right-0">
            <button className="btn btn-success text-success-content mr-2 normal-case" onClick={
                () => {
                    onEditPlaceSaveClick()
                }
                } >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path className="stroke-success-content" d="M8 20H6C4.89543 20 4 19.1046 4 18V6C4 4.89543 4.89543 4 6 4H9M8 20V14C8 13.4477 8.44772 13 9 13H15C15.5523 13 16 13.4477 16 14V20M8 20H16M16 20H18C19.1046 20 20 19.1046 20 18V8.82843C20 8.29799 19.7893 7.78929 19.4142 7.41421L16.5858 4.58579C16.2107 4.21071 15.702 4 15.1716 4H15M15 4V7C15 7.55228 14.5523 8 14 8H10C9.44772 8 9 7.55228 9 7V4M15 4H9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>    
                Save
            </button>

            <button className="btn btn-circle btn-soft" onClick={
                    () => {
                        onEditPlaceModalDismiss()
                    }
                    }>
                <svg className="fill-base-content w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M19.207 6.207a1 1 0 0 0-1.414-1.414L12 10.586 6.207 4.793a1 1 0 0 0-1.414 1.414L10.586 12l-5.793 5.793a1 1 0 1 0 1.414 1.414L12 13.414l5.793 5.793a1 1 0 0 0 1.414-1.414L13.414 12l5.793-5.793z"/></svg>
            </button>
        </div>
        {
                fullScreenLoadingText &&
                <div className='w-full h-screen absolute max-w-[100%] top-0 flex flex-row items-center justify-center gap-2 backdrop-blur-sm bg-base-100/30 z-50'>
                    <div className="loading loading-infinity loading-lg"></div>
                    <div className='font-normal text-base-content'>{fullScreenLoadingText}</div>
                </div>
        }
        {
                toastMessage && <div className="toast z-10">
                <div className={`alert ${toastMessage.type == "success" ? "alert-success" : "alert-info"}`}>
                <span className="text-sm">{toastMessage.message}</span>
                </div>
            </div>
        }
        {
            <dialog id="pick-place-emoji-modal" className="modal">
                <div className="modal-box w-max">
                    <h3 className="font-bold text-lg pb-4">Pick Place Emoji</h3>
                    <Picker data={data} onEmojiSelect={(emoji) => { onEmojiSelected(emoji.native) }} />
                    <div className="modal-action">
                    <button className="btn btn-circle btn-soft" onClick={
                            () => {
                                document.getElementById("pick-place-emoji-modal").close()
                            }
                            }>
                        <svg className="fill-base-content w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M19.207 6.207a1 1 0 0 0-1.414-1.414L12 10.586 6.207 4.793a1 1 0 0 0-1.414 1.414L10.586 12l-5.793 5.793a1 1 0 1 0 1.414 1.414L12 13.414l5.793 5.793a1 1 0 0 0 1.414-1.414L13.414 12l5.793-5.793z"/></svg>
                    </button>
                    </div>
                </div>
            </dialog>
        }
        </div>
        </Sheet.Scroller>
        </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop onTap={(_) => {
            onEditPlaceModalDismiss()
        }}/>
    </Sheet>
    {
        isLocationPickerModalOpen && <LocationPickerModal existingPlace={selectedLocation} isLocationPickerModalOpen={isLocationPickerModalOpen} onLocationPickerModalDismiss={onLocationPickerModalDismiss} onLocationSelected={onLocationSelected} />
    }
    </>
}